import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { CardBody, Card } from "reactstrap";
// components
import ALoader from "views/pages/manage/common/a-loader";
import DepositedList from "../components/DepositedList";
import StudentSearchBar from "modules/paymentManagement/cash-management/components/StudentSearchBar";
import TopHeaderBar from "modules/paymentManagement/cash-management/components/TopHeaderBar";
import DocumentViewer from "modules/paymentManagement/cash-management/receipt/components/DocumentViewerModal";
import DepositedReceiptFiltersDrawer from "../components/DepositedReceiptFiltersDrawer";
import ChequeCancelledSuccess from "../components/ChequeCancelledSuccess";
import SuccessMessageAlert from "../components/SuccessMessageAlert";
// hooks
import { useFetchList } from "modules/paymentManagement/cash-management/hooks/useFetchList";
import { updateRequest } from "../../../../../views/pages/manage/common/utils/methods/commonMethods/utilityMethod";
import { useModal } from "../../../../../contexts/customModal";
import { useFetchDropdown } from "modules/paymentManagement/cash-management/receipt/hooks/useDropdown";
import { useDepositCheque } from "../../hooks/useDepositCheque";

import { useNavigateToGivenPath } from "views/pages/manage/edpOps/studentDetails/paymentSummary/service";
// helpers
import "modules/paymentManagement/cash-management/styles/cashManagement.scss";
import { depositedListFilter } from "../../utils";
import { getFilterDate } from "modules/paymentManagement/approvalDashboard/shared/constant";
import CheckDateExtensionSuccess from "../../../cash-management/receipt/components/CheckDateExtension";
import ChequeDateExtensionForm from "../components/ChequeDateExtensionForm";
import { getAllChequeReceiptListApi } from "../../receipts/api";
import { useBuTypeNameByBuId } from "modules/paymentManagement/cash-management/hooks/useFetchBuType";
const SEARCH_STYLE = { fontSize: "12px" };
const SEARCH_BY = [
  { key: "psid", value: "By PSID" },
  { key: "referenceNumber", value: "By Cheque Number" },
];

// Defined outside to prevent re-creation
const ViewChequeDepositPage = () => {
  // 1. URL & Navigation
  const { id: businessAreaId, cashierOffice } = useParams();
  const navigate = useNavigateToGivenPath();
  const [viewer, setViewer] = useState({
    open: false,
    s3key: "",
  });
  const [state, setState] = useState(); // State to manage API response state

  // Memoize navigation to prevent recreation
  const goViewDeposit = useCallback(
    () =>
      navigate(
        `/admin/cheque-management/viewChequeDeposit/${businessAreaId}/${cashierOffice}`
      ),
    [navigate, businessAreaId]
  );
  
   const goBackToChequeManagement = () => {
    navigate(`/admin/cheque-management`);
  };

  const onEditReceipt = (row) => {
    showModal({
      title: `Cheque date extension`,
      content: (
        <ChequeDateExtensionForm refreshList={refreshReceiptList} row={row} />
      ),
      maxWidth: "lg",
    });
  };

  // 2. Data Fetching
  const [isDropdownLoading, setIsDropdownLoading] = useState(false);
  const dropdownValues = useFetchDropdown();

  const { depositCheque, loading: isSubmitting } =
    useDepositCheque(setIsDropdownLoading);

  // 3. List Management
  const [searchInput, setSearchInput] = useState("");
  const [searchBy, setSearchBy] = useState("psid");

  const {
    rows,
    loading: isListLoading,
    pagination,
    onPageChange,
    onPageSizeChange,
    filters,
    onFilterApply,
    fetchTrackerTimeLine,
  } = useFetchList(getAllChequeReceiptListApi, {
    initialPageSize: 100, // Consider lowering this
    businessAreaId,
    cashierOffice,
    initialFilters: {},
  }, setState);

  const updatedPagination = useMemo(
    () => ({
      ...pagination,
      page: pagination.page + 1, // Adjusting for 1-based UI
    }),
    [pagination]
  );

  const [selectedIds, setSelectedIds] = useState(new Set());

  // Safety: specific to current page data to prevent calculation errors
  const { selectedReceipts, selectedTotal } = useMemo(() => {
    if (!selectedIds.size) return { selectedReceipts: [], selectedTotal: 0 };

    // Only map items currently visible in rows to ensure data integrity
    const selectedItems = rows.filter((item) => selectedIds.has(item.id));
    const total = selectedItems.reduce(
      (acc, item) => acc + item.amountCollected,
      0
    );

    return { selectedReceipts: selectedItems, selectedTotal: total };
  }, [selectedIds, rows]);

  // Logic: Reset selection when page changes to prevent "Ghost selections"
  useEffect(() => {
    setSelectedIds(new Set());
  }, [pagination.page]);

  const { showModal, hideModal } = useModal();

  const refreshReceiptList = useCallback(
    (response, row, type = "CANCEL") => {
      onFilterApply({});
      setSelectedIds(new Set());

      switch (type) {
        case "BOUNCE":
          showModal({
            title: `Cheque/DD Bounce Successful`,
            content: (
              <SuccessMessageAlert
                onClose={hideModal}
                details={response}
                isCheque={true}
                onView={goViewDeposit}
                isChequeBounced={true}
                row={row}
              />
            ),
            maxWidth: "lg",
          });
          break;
        case "CANCEL":
          showModal({
            title: `Cheque cancelled`,
            content: (
              <ChequeCancelledSuccess
                onClose={hideModal}
                details={response}
                isCheque={true}
                onView={goViewDeposit}
                row={row}
              />
            ),
            maxWidth: "lg",
          });
          break;
        case "DATE_EXTENSION":
          showModal({
            title: `Cheque date extension sent for approval`,
            content: (
              <CheckDateExtensionSuccess
                onClose={hideModal}
                details={response}
                isCheque={true}
                onView={goViewDeposit}
                row={row}
              />
            ),
            maxWidth: "lg",
          });
          break;
        default:
          break;
      }
    },
    [onFilterApply, showModal, goViewDeposit, hideModal]
  );

  // 6. Filter Handlers
  const applyFilterHandler = useCallback(
    (newFilter) => {
      const formateRequest = {
        ...newFilter,
        chequeDateFrom: getFilterDate(newFilter?.chequeDateFrom, "start"),
        chequeDateTo: getFilterDate(newFilter?.chequeDateTo, "end"),
      };
      onFilterApply(updateRequest(formateRequest));
    },
    [onFilterApply]
  );

  const applyPrimaryFilter = useCallback(() => {
    onFilterApply({ [searchBy]: searchInput });
  }, [onFilterApply, searchBy, searchInput]);

  const removeFilterFormHandler = useCallback(() => {
    onFilterApply({});
  }, [onFilterApply]);

  const { buTypeName, loading } = useBuTypeNameByBuId(businessAreaId)
  const headerData = useMemo(
    () => ({
      Type: buTypeName,
      BU: businessAreaId,
      "Cashier's office": cashierOffice,
    }),
    [businessAreaId, cashierOffice, buTypeName]
  );

  const onViewCheque = (row) => {
    setViewer({ open: true, s3key: row?.attachmentS3Key });
  };

  const statusChipChangeHandler = (value) => {
    if (value === "all") {
      applyFilterHandler({ status: [] });
      return;
    }
    applyFilterHandler({ status: [value] });
  };

  return (
    <>
      <div className="remove-container-top-space">
        <TopHeaderBar
          title="List of all cheque collection"
          headerData={headerData}
          onBackClick={goBackToChequeManagement}
        />

        <div className="cm-container">
          {(isSubmitting || isListLoading || isDropdownLoading) && (
            <ALoader position="fixed" />
          )}

          <Card>
            <CardBody>
              <div className="d-flex justify-content-between mb-3">
                <div className="heading-3 color-black semi-bold">
                  Cheque list{" "}
                </div>

                <div className="d-flex gap-md">
                  <div>
                    <StudentSearchBar
                      searchValue={searchInput}
                      onChange={setSearchInput}
                      onKeyDown={applyPrimaryFilter}
                      onSearchClick={applyPrimaryFilter}
                      dropdownValue={searchBy}
                      onDropdownChange={setSearchBy}
                      dropdownStyles={SEARCH_STYLE}
                      dropdownOptions={SEARCH_BY}
                    />
                  </div>
                  {/* <AToggleButtonGroup
                    changeHandler={statusChipChangeHandler}
                    options={depositedListFilter}
                    defaultSelected="all"
                    // isApplyHandler={isApplyHandler()}
                  /> */}

                  <DepositedReceiptFiltersDrawer
                    applyFilterHandler={applyFilterHandler}
                    appliedForm={filters}
                    dropdownValues={dropdownValues}
                    onRemoveFilterHandler={removeFilterFormHandler}
                  />
                </div>
              </div>

              <DepositedList
                rows={rows}
                loading={isListLoading}
                pagination={updatedPagination}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                onViewCheque={onViewCheque}
                openModal={showModal}
                hideModal={hideModal}
                onEditReceipt={onEditReceipt}
                refreshList={refreshReceiptList}
                fetchTrackerTimeLine={fetchTrackerTimeLine}
                setState={setState}
                state={state}
              />
            </CardBody>
          </Card>
        </div>
      </div>

      <DocumentViewer
        open={viewer.open}
        onClose={() => setViewer({ open: false, s3key: "" })}
        s3Key={viewer.s3key}
      />
    </>
  );
};

export default ViewChequeDepositPage;
